import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Settings } from 'lucide-react';
import { toast } from 'sonner';
import ChatContainer from '../components/chat/ChatContainer';
import { SpeakingProvider } from '../contexts/SpeakingContext';
import type { ChatMessage, SuggestionItem, EnhancedQAApiResponse } from '../types/chat';
import { QAService } from '../services/qa-service';
import { ApiService } from '../services/api';
import { clearOldDays, loadTodayMessages, saveTodayMessages, scheduleDailyChatCacheCleanup, hasCleanedToday, markTodayCleaned, removePerMessageKeysForToday } from '../services/chatStorage';

// Initial welcome messages
const initialMessages: ChatMessage[] = [
  {
    id: '1',
    content: 'Xin chào! Tôi là trợ lý AI của bạn. Hãy đặt câu hỏi về các thủ tục hành chính và tôi sẽ giúp bạn tìm thông tin cần thiết.',
    sender: 'ai',
    timestamp: new Date()
  }
];

const ChatPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isAuthenticated, checkAuthStatus } = useAuth();
  const todayId = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}${mm}${dd}`;
  };
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try {
      if (!hasCleanedToday()) {
        clearOldDays();
        markTodayCleaned();
      }
      removePerMessageKeysForToday();
      const cached = loadTodayMessages();
      if (cached && cached.length > 0) {
        return cached.map(m => ({ ...m, restored: true }));
      }
      return initialMessages;
    } catch {
      return initialMessages;
    }
  });
  const [isTyping, setIsTyping] = useState(false);
  const [started, setStarted] = useState<boolean>(() => {
    try {
      return localStorage.getItem('kiosk-started-date') === todayId();
    } catch {
      return false;
    }
  });
;  const [startSignal, setStartSignal] = useState<number>(0);



  // Initialize QA service
  const qaService = new QAService();

  // Helper function to generate content based on response type
  const getResponseContent = useCallback((responseData: EnhancedQAApiResponse): string => {
    switch (responseData.response_type) {
      case 'direct_answer':
        return responseData.answer || 'Không có câu trả lời';
      case 'suggestions':
        return '';
      case 'search_recommended':
        return 'Tôi không tìm thấy thông tin chính xác. Bạn có thể thử tìm kiếm với các từ khóa sau:';
      default:
        return responseData.answer || 'Không có câu trả lời';
    }
  }, []);

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  // Handle API errors
  const handleApiError = useCallback((error: Error): string => {
    if (error.message === 'Authentication required') {
      return t('chat.api.errors.authenticationFailed');
    }
    if (error.message.includes('429')) {
      return t('chat.api.errors.tooManyRequests');
    }
    if (error.message.includes('500')) {
      return t('chat.api.errors.serviceUnavailable');
    }
    if (error.message.includes('timeout')) {
      return t('chat.api.errors.timeout');
    }
    return error.message || t('chat.api.errors.unexpectedError');
  }, [t]);

  const handleSendMessage = useCallback(async (content: string) => {
    // Check authentication before sending
    if (!checkAuthStatus()) {
      toast.error('Authentication required. Please authorize your device.');
      return;
    }

    // Add user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    try {
      // Call enhanced QA API service
      const response = await qaService.askQuestionEnhanced(content);

      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to get response from API');
      }

      const responseData = response.data;

      // Create AI message based on response type
      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: getResponseContent(responseData),
        sender: 'ai',
        timestamp: new Date(),
        confidence: responseData.confidence || responseData.max_confidence || 0,
        sources: responseData.sources?.map(source => ({
          id: source.id,
          title: source.title,
          url: source.url,
          excerpt: source.excerpt,
          confidence: source.confidence
        })) || [],
        documents: responseData.documents || [],
        responseType: responseData.response_type,
        suggestions: responseData.suggestions,
        questionId: responseData.question_id,
        summary: responseData.summary
      };

      setMessages(prev => [...prev, aiMessage]);

    } catch (error) {
      console.error('Chat error:', error);
      
      const errorMessage = handleApiError(error as Error);
      const errorAiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: `Xin lỗi, đã xảy ra lỗi không mong muốn: ${errorMessage}`,
        sender: 'ai',
        timestamp: new Date(),
        confidence: 0
      };

      setMessages(prev => [...prev, errorAiMessage]);
      toast.error(errorMessage);

    } finally {
      setIsTyping(false);
    }
  }, [checkAuthStatus, handleApiError, qaService, getResponseContent]);

  // Handle suggestion selection with exact query
  const handleSelectSuggestion = useCallback(async (suggestion: SuggestionItem) => {
    if (!checkAuthStatus()) {
      toast.error('Authentication required. Please authorize your device.');
      return;
    }

    // First, add the suggestion title as a user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: suggestion.title,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    try {
      // Call the exact query API for more precise results
      const response = await qaService.askExactQuestion(suggestion.title);

      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to get response from API');
      }

      const responseData = response.data;

      // Create AI message based on response type
      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: responseData.answer || getResponseContent(responseData),
        sender: 'ai',
        timestamp: new Date(),
        confidence: responseData.confidence || responseData.max_confidence || 0,
        sources: responseData.sources?.map(source => ({
          id: source.id,
          title: source.title,
          url: source.url,
          excerpt: source.excerpt,
          confidence: source.confidence
        })) || [],
        documents: responseData.documents || [],
        responseType: responseData.response_type,
        suggestions: responseData.suggestions,
        questionId: responseData.question_id,
        showSuggestionsOnlyTitles: responseData.show_suggestions_only_titles,
        isExactQuery: responseData.is_exact_query,
        summary: responseData.summary
      };

      setMessages(prev => [...prev, aiMessage]);

    } catch (error) {
      console.error('Suggestion selection error:', error);
      
      const errorMessage = handleApiError(error as Error);
      const errorAiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: `Xin lỗi, đã xảy ra lỗi khi xử lý câu hỏi của bạn: ${errorMessage}`,
        sender: 'ai',
        timestamp: new Date(),
        confidence: 0
      };

      setMessages(prev => [...prev, errorAiMessage]);
      toast.error(errorMessage);

    } finally {
      setIsTyping(false);
    }
  }, [checkAuthStatus, handleApiError, qaService, getResponseContent]);

  // Handle search recommendation
  const handleSearchRecommendation = useCallback(async (query: string) => {
    // Treat search recommendation as a new question
    await handleSendMessage(query);
  }, [handleSendMessage]);

  // Check API service health on component mount
  useEffect(() => {
    const checkServiceHealth = async () => {
      try {
        if (checkAuthStatus()) {
          const status = await ApiService.getStatus();
          console.log('QA API service status:', status);
        }
      } catch (error) {
        console.error('Health check error:', error);
      }
    };

    checkServiceHealth();
  }, [checkAuthStatus]);

  // cache loaded in lazy initializer

  useEffect(() => {
    const id = scheduleDailyChatCacheCleanup();
    return () => { try { if (id) clearInterval(id) } catch {} };
  }, []);

  const handleStart = useCallback(() => {
    try { localStorage.setItem('kiosk-started-date', todayId()); } catch {}
    setStarted(true);
    try {
      if (messages.length === 1 && messages[0].sender === 'ai') {
        setStartSignal(s => s + 1);
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      saveTodayMessages(messages);
    } catch {}
  }, [messages]);

  useEffect(() => {
    try {
      const now = Date.now();
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i) || '';
        if (k.startsWith('tts-cache-')) {
          const raw = localStorage.getItem(k);
          if (!raw) continue;
          try {
            const data = JSON.parse(raw) as { ts?: number };
            if (!data.ts || now - data.ts > 86400000) {
              localStorage.removeItem(k);
            }
          } catch {
            localStorage.removeItem(k);
          }
        }
      }
    } catch {}
  }, []);

  return (
    <div className="h-screen bg-kiosk-background flex flex-col">
      {!started && (
        <div className="fixed inset-0 z-50 bg-white/90 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center p-6 rounded-xl shadow-sm bg-white border">
            <h2 className="text-2xl font-bold text-kiosk-primary mb-3">Chào mừng</h2>
            <p className="text-gray-600 mb-6">Nhấn "Bắt đầu" để vào giao diện chat</p>
            <Button
              variant="secondary"
              size="kiosk-lg"
              onClick={handleStart}
              className="kiosk-touch-target border border-gray-300 shadow-sm"
            >
              Bắt đầu
            </Button>
          </div>
        </div>
      )}
      {/* Header with settings button */}
      <div className="bg-white shadow-sm border-b flex-shrink-0">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-kiosk-primary">
            Chat Kiosk
          </h1>
          <Button
            variant="outline"
            size="lg"
            onClick={handleSettingsClick}
            className="kiosk-touch-target"
          >
            <Settings className="w-5 h-5 mr-2" />
            {t('settings.title')}
          </Button>
        </div>
      </div>

      {/* Chat interface */}
      <div className="flex-1 px-4 overflow-hidden container mx-auto">
        <div className="h-full kiosk-card">
          <SpeakingProvider>
            <ChatContainer
              messages={messages}
              isTyping={isTyping}
              onSendMessage={handleSendMessage}
              onSelectSuggestion={handleSelectSuggestion}
              onSearchRecommendation={handleSearchRecommendation}
              className="h-full"
              startSignal={startSignal}
            />
          </SpeakingProvider>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
